/**
 * MYID-D5 — i18n bootstrap for my.collabkare.id.
 *
 * Lifted from karePortal apps/next/src/i18n/index.ts (KP9-1 + KP9-5).
 * Same library choices (react-i18next + i18next + browser-languagedetector),
 * same URL strategy (localStorage-only, no /en/* prefix), same KC locale
 * claim hook (added in KP9-18 / pickup once a LanguagePicker lands here).
 *
 * Namespaces tuned for the account-portal domain (different from
 * karePortal's clinical-portal namespaces):
 *
 *   - common    — shared (buttons, dialogs, generic UI)
 *   - nav       — sidebar + top header labels
 *   - account   — profile / security / consents / org / licenses
 *   - billing   — subscriptions / credits / orders / purchases / verification
 *   - errors    — error messages / toast strings / 4xx-5xx surfaces
 *
 * D5 ships only the EN source bundles (seeded mostly empty). D6 adds
 * Spanish, D7 adds Telugu + Noto Sans Telugu webfont. A LanguagePicker
 * UI is a separate follow-up (paralleling KP9-3); until it lands, the
 * detector falls through to navigator-default, which Just Works for
 * Latin-script users today.
 *
 * Adding a new language: drop locales/<lang>/{common,nav,account,billing,
 * errors}.json, import + register in `resources` below, then add to
 * SUPPORTED_LANGUAGES.
 *
 * Adding a new namespace: create locales/en/<ns>.json (+ es + te),
 * import + register, add to NAMESPACES, update i18next-parser.config.js.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// ─── English source dictionaries (D5 — seeded empty) ────────────────────
import enCommon from "@/locales/en/common.json";
import enNav from "@/locales/en/nav.json";
import enAccount from "@/locales/en/account.json";
import enBilling from "@/locales/en/billing.json";
import enErrors from "@/locales/en/errors.json";

// ─── Spanish dictionaries (D6 — seeded empty stubs, populated later) ────
import esCommon from "@/locales/es/common.json";
import esNav from "@/locales/es/nav.json";
import esAccount from "@/locales/es/account.json";
import esBilling from "@/locales/es/billing.json";
import esErrors from "@/locales/es/errors.json";

// ─── Telugu dictionaries (D7 — seeded empty stubs, populated later) ─────
import teCommon from "@/locales/te/common.json";
import teNav from "@/locales/te/nav.json";
import teAccount from "@/locales/te/account.json";
import teBilling from "@/locales/te/billing.json";
import teErrors from "@/locales/te/errors.json";

export const NAMESPACES = [
  "common",
  "nav",
  "account",
  "billing",
  "errors",
] as const;
export type Namespace = (typeof NAMESPACES)[number];

/**
 * SUPPORTED_LANGUAGES drives the future LanguagePicker UI.
 * `available: true` = resource bundles registered.
 * `available: false` = picker chip shown disabled with "Coming soon".
 */
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English", available: true },
  { code: "es", label: "Spanish", nativeLabel: "Español", available: true },
  { code: "te", label: "Telugu", nativeLabel: "తెలుగు", available: true },
  { code: "hi", label: "Hindi", nativeLabel: "हिन्दी", available: false },
  { code: "ta", label: "Tamil", nativeLabel: "தமிழ்", available: false },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const AVAILABLE_LANGUAGE_CODES: SupportedLanguageCode[] =
  SUPPORTED_LANGUAGES.filter((l) => l.available).map((l) => l.code);

export const I18N_LOCALSTORAGE_KEY = "myCollabkareId:locale";

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        nav: enNav,
        account: enAccount,
        billing: enBilling,
        errors: enErrors,
      },
      es: {
        common: esCommon,
        nav: esNav,
        account: esAccount,
        billing: esBilling,
        errors: esErrors,
      },
      te: {
        common: teCommon,
        nav: teNav,
        account: teAccount,
        billing: teBilling,
        errors: teErrors,
      },
    },

    fallbackLng: "en",
    supportedLngs: AVAILABLE_LANGUAGE_CODES,
    // Pin to base codes — "es-MX" → "es", not "en" fallback.
    nonExplicitSupportedLngs: true,
    load: "languageOnly",

    ns: [...NAMESPACES],
    defaultNS: "common",
    fallbackNS: "common",

    detection: {
      // Order matters — localStorage trumps navigator so explicit picker
      // choice (when the picker ships) sticks across reloads.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: I18N_LOCALSTORAGE_KEY,
      caches: ["localStorage"],
    },

    interpolation: {
      // React already escapes — don't double-encode.
      escapeValue: false,
    },

    // Dev: warn on missing keys so extraction stays honest.
    debug: import.meta.env.DEV,
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: import.meta.env.DEV
      ? (lngs: readonly string[], ns: string, key: string) => {
          // eslint-disable-next-line no-console
          console.warn(
            `[i18n] missing key — lng=${lngs.join(",")} ns=${ns} key=${key}`,
          );
        }
      : undefined,

    react: {
      // Resources are static-imported above; ready synchronously.
      useSuspense: false,
    },
  })
  .then(() => {
    // KP9-5 lift — sync <html lang> + <html dir> on init.
    applyDocumentLocale();
  });

i18n.on("languageChanged", applyDocumentLocale);

function applyDocumentLocale() {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!root) return;
  const lng = i18n.resolvedLanguage || "en";
  root.setAttribute("lang", lng);
  root.setAttribute("dir", i18n.dir(lng));
}

export default i18n;
